import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  FlexSpace,
  LinkTab,
  LinksContainer,
  FlexColumn,
  Header2SemiBold,
  ImageField as BaseImageField,
  card,
  TimeAgoRenderer,
  Body,
  Tooltip as BaseTooltip,
  Flex,
  FlexColumnCenter,
  Modal,
  Small,
  PromptClick,
  CircleIcon,
  Button
} from '@ubeya/shared-web/components';
import { responsiveHide, responsiveShow } from '@ubeya/shared-web/style';
import { ReactComponent as NoGenderIcon } from '@ubeya/shared-web/assets/no-gender.svg';
import { ReactComponent as ErrorIcon } from '@ubeya/shared-web/assets/error.svg';
import { ReactComponent as StarIcon } from '@ubeya/shared-web/assets/star.svg';
import { ReactComponent as StarIconFilled } from '@ubeya/shared-web/assets/star-filled-primary.svg';
import { ReactComponent as DateIcon } from '@ubeya/shared-web/assets/SidebarIcons/Submodules/timesheet.svg';
import { ReactComponent as CreatedIcon } from '@ubeya/shared-web/assets/recent-actions.svg';
import { ReactComponent as CalendarIcon } from '@ubeya/shared-web/assets/calendar.svg';
import { ReactComponent as NoShowIcon } from '@ubeya/shared-web/assets/no-show-red.svg';
import { ReactComponent as CodeIcon } from '@ubeya/shared-web/assets/invoice.svg';
import { ReactComponent as BaseActivateIcon } from '@ubeya/shared-web/assets/confirmation.svg';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import { ReactComponent as VerifiedIcon } from '@ubeya/shared-web/assets/verified.svg';
import { ReactComponent as WarningIcon } from '@ubeya/shared-web/assets/photo-warning.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { useField } from 'react-final-form';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useEmployeeKpiStats from '@ubeya/shared/hooks/employees/useEmployeeKpiStats';
import Actions from './Actions';

const InfoText = styled(Body)`
  margin-left: 4px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const Tooltip = styled(BaseTooltip)`
  .rc-tooltip-inner {
    ${({ $maxWidth }) =>
      $maxWidth &&
      css`
        max-width: ${$maxWidth}px;
      `}
    text-align: center;
  }
`;

const ActivateIcon = styled(BaseActivateIcon)`
  path {
    fill: ${({ theme }) => theme.colors.greenPositive};
  }
`;

const EXISTS_EMPLOYEE_TAB = [
  'profile',
  'hiringProcess',
  'contracts',
  'contractTypes_leaveManagement',
  'leaveManagementV2',
  'generalAvailability',
  'weeklyPreferences',
  'wages',
  'jobs',
  'applicationsAndRejections',
  'documents',
  'auditLogs',
  'comments',
  'compliance',
  'personalSettings',
  'workingTimeDirective',
  'awr',
  'ticketsHistory'
];
const NEW_EMPLOYEE_TAB = ['profile', 'wages', 'personalSettings', 'contracts'];

const TabsWrapper = styled(FlexSpace)`
  ${card};
  padding: 0 30px 12px 30px;
  z-index: 3;

  ${responsiveHide.tablet}
`;

const CircleChip = styled(ErrorIcon)`
  width: 16px;
  height: 16px;
  margin-left: 5px;
  margin-top: 5px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding: 4px 0px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const JustifyRow = styled(FlexColumnCenter)`
  position: relative;
  width: 100%;
  padding: 16px 0;
  margin: 0 16px;

  ${({ $isNew }) =>
    !$isNew &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
    `}
`;

const RatingRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin-top: 8px;
`;

const SideWrapper = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.grey1};
  align-items: center;
  padding: 24px 16px 0 16px;
  position: absolute;
  top: 0;
  left: 0;
  width: 260px;
  bottom: 0;
  z-index: 5;
  ${responsiveShow.tablet}
  ${({ isLoading }) => isLoading && 'opacity: 0.4;'}
`;

const Scrollable = styled(FlexColumn)`
  width: 100%;
  overflow: auto;
  padding: 8px 0;
`;

const ImageField = styled(BaseImageField)`
  border: none;
  width: 120px;
  height: 120px;
  min-height: 120px;
  pointer-events: none;
  border-radius: 50%;
  overflow: hidden;
`;

const Title = styled(Header2SemiBold)`
  text-align: center;
`;

const Links = styled(FlexColumn)`
  width: 100%;
  margin-top: 8px;
`;

const ImageModal = styled(Modal)`
  .modal-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: calc(80vh + 120px);
    padding: 0;
    overflow: hidden;
  }

  border: none;
`;

const ModalHeader = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const ModalTitle = styled(Header2SemiBold)`
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const CloseButton = styled(CircleIcon)`
  &&& {
    width: 40px;
    height: 40px;
    padding: 12px;
  }
`;

const ModalContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0;
    display: block;
  }

  .image-modal-field {
    height: 100%;
    width: 100%;
    border: none;
    object-fit: contain;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 0;
  }
`;

const ModalFooter = styled.div`
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey2};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-shrink: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
`;

const Link = styled(Body)`
  min-height: 40px;
  display: flex;
  align-items: center;
  color: ${({ theme, isActive }) => (isActive ? theme.colors.black : theme.colors.grey4)};
  border-top: 1px solid ${({ theme }) => theme.colors.grey2};
  ${({ isActive }) => isActive && 'font-weight: 500'};

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  }
  cursor: pointer;
  text-align: left;
  padding: 0 8px;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBackground};
  }
`;

const ProfileImage = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  padding: 3px;
  border: 2px solid transparent;
  transition: all 0.2s ease;

  ${({ $hasImage, $isVerified, theme }) =>
    $hasImage &&
    $isVerified !== undefined &&
    css`
      border-color: ${$isVerified ? theme.colors.greenPositive : theme.colors.redNegative};
    `}

  .profile-badge {
    position: absolute;
    width: 53px;
    height: 53px;
    right: -10px;
    bottom: -10px;
    z-index: 2;
  }
`;

const ProfileImageHoverTarget = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
`;

const VerificationBadge = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: ${({ $isVerified }) => ($isVerified ? '0.5px 8px 0.5px 2px' : '2px 8px 2px 2px')};
  border-radius: 100px;
  border: 1.5px solid
    ${({ theme, $isVerified }) => ($isVerified ? theme.colors.greenPositive : theme.colors.redNegative)};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme, $isVerified }) =>
      $isVerified ? `${theme.colors.greenPositive}10` : `${theme.colors.redNegative}10`};
  }
`;

const VerifiedPhotoIcon = styled(VerifiedIcon)`
  width: 20px;
  height: 20px;
  flex: none;
`;

const UnverifiedPhotoIcon = styled(WarningIcon)`
  width: 16px;
  height: 16px;
  flex: none;
`;

const PhotoVerificationText = styled(Small)`
  color: ${({ theme, $isVerified }) => ($isVerified ? theme.colors.greenPositive : theme.colors.redNegative)};
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
`;

const PhotoButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 40px;
  padding: 0 24px;
`;

const VerificationContainer = styled(FlexColumn)`
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const ProfileImageWrapper = styled(FlexColumn)`
  align-items: center;
  gap: 5px;
`;

const JobStatsRows = ({ employeeId, firstName }) => {
  const { t } = useContext(ConfigContext);
  const { shiftsWorked, noShows, noShowDenominator } = useEmployeeKpiStats(employeeId);

  return (
    <>
      <Tooltip body={t('kpiShiftsWorkedTooltip', { firstName })}>
        <Row>
          <CalendarIcon />
          <InfoText>{t('shiftsWorkedLine', { count: shiftsWorked })}</InfoText>
        </Row>
      </Tooltip>
      {noShows > 0 && (
        <Tooltip
          body={t('employeeNoShowTooltip', {
            firstName,
            noShowsCount: noShows,
            shiftCount: noShowDenominator
          })}>
          <Row>
            <NoShowIcon />
            <InfoText>{t('noShowsLine', { count: noShows })}</InfoText>
          </Row>
        </Tooltip>
      )}
    </>
  );
};

const Tabs = ({
  isLoading,
  activeTab,
  setActiveTab,
  mode,
  weeklyPreference,
  recurringAvailability,
  handleClose,
  inviteEmployee,
  id: employeeId,
  deleteEmployee,
  firstName,
  lastName,
  isDeleted,
  isPending,
  isActive,
  phone,
  userId,
  accountId,
  lastActivity,
  createdAt,
  isPushNotification,
  rating,
  jobsRatingCount,
  code,
  showPayrollTab,
  showHiringTab,
  showContractsTab,
  showLeaveManagementTab,
  showLeaveManagementV2Tab,
  employeeFields,
  updateEmployee,
  isLoadingUpdateEmployee
}) => {
  const { t, isShowLastNameFirst = false } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const dropzoneRef = useRef(null);

  const {
    input: { value: image, onChange: setImage }
  } = useField('image');
  const {
    input: { value: isVerifiedValue, onChange: setIsVerified }
  } = useField('isVerified', { subscription: { value: true } });

  const isVerified = isVerifiedValue === true;

  const [uploadedPreview, setUploadedPreview] = useState(null);
  const currentImage = uploadedPreview?.image === image ? uploadedPreview.blob : image;

  const { isUbeyaAdmin } = useUser();
  const { modules } = useModules();
  const hasHelpDesk = !!modules?.help_desk;

  const {
    account: { config: { isPhotoVerificationEnabled } = {} }
  } = useAccountConfig();

  const { addResponseError } = useToaster();

  const tabs = (mode === 'ADD' ? NEW_EMPLOYEE_TAB : [...EXISTS_EMPLOYEE_TAB])
    .filter(Boolean)
    .filter((tab) => {
      if (tab === 'wages') {
        return showPayrollTab;
      }

      if (tab === 'hiringProcess') {
        return showHiringTab;
      }

      if (tab === 'awr' && !isUbeyaAdmin) {
        return false;
      }

      if (tab === 'ticketsHistory' && !hasHelpDesk) {
        return false;
      }

      if (tab === 'compliance' && !myPrivileges[privileges.COMPLIANCE]) {
        return false;
      }

      if (tab === 'generalAvailability') {
        return !!recurringAvailability;
      }

      if (tab === 'weeklyPreferences' || tab === 'personalSettings') {
        return !!weeklyPreference;
      }
      if (tab === 'contracts') {
        return showContractsTab;
      }
      if (tab === 'contractTypes_leaveManagement') {
        return showLeaveManagementTab;
      }
      if (tab === 'leaveManagementV2') {
        return showLeaveManagementV2Tab;
      }

      return true;
    })
    .map((key) => ({
      key,
      label: t(key)
    }));

  const actions = useMemo(
    () => (
      <Actions
        {...{
          mode,
          employeeId,
          employeeFields,
          handleClose,
          inviteEmployee,
          isDeleted,
          isActive,
          deleteEmployee,
          id: userId,
          phone,
          accountId
        }}
      />
    ),
    [
      mode,
      handleClose,
      employeeFields,
      employeeId,
      inviteEmployee,
      isDeleted,
      isActive,
      deleteEmployee,
      userId,
      phone,
      accountId
    ]
  );

  const lastActivityProps = useMemo(() => ({ rowData: { lastActivity }, dataKey: 'lastActivity' }), [lastActivity]);
  const createdAtProps = useMemo(() => ({ rowData: { createdAt }, dataKey: 'createdAt' }), [createdAt]);

  const employeeDisplayName = useMemo(() => getEmployeeDisplayName(firstName, lastName, isShowLastNameFirst), [
    firstName,
    lastName,
    isShowLastNameFirst
  ]);

  const handleProfileImageClick = useCallback(() => {
    if (image) {
      setIsImageModalOpen(true);
    } else {
      dropzoneRef.current?.open();
    }
  }, [image, dropzoneRef]);

  const handleTogglePhotoVerification = useCallback(async () => {
    if (!image || !accountId || !employeeId) return;

    try {
      await updateEmployee({
        accountId,
        employeeId,
        isVerified: !isVerified,
        expectedImage: image,
        invalidateEmployee: true
      });

      setIsVerified(!isVerified);
      setIsImageModalOpen(false);
    } catch (error) {
      addResponseError(error);
    }
  }, [image, isVerified, accountId, employeeId, updateEmployee, setIsVerified, addResponseError]);

  const handleImageChange = useCallback(
    async (value, blob) => {
      const previousImage = image;
      setImage(value);
      setUploadedPreview({ image: value, blob });

      if (isPhotoVerificationEnabled && !previousImage && value) {
        setIsVerified(true);
      }

      // If we're in the modal and a new photo was selected (different from previous), save it immediately
      if (isImageModalOpen && value && value !== previousImage && accountId && employeeId) {
        try {
          await updateEmployee({
            accountId,
            employeeId,
            image: value,
            invalidateEmployee: true
          });
        } catch (error) {
          // Error handled by backend response
        }
      }
    },
    [
      isImageModalOpen,
      image,
      accountId,
      employeeId,
      updateEmployee,
      setImage,
      isPhotoVerificationEnabled,
      setIsVerified
    ]
  );

  return (
    <>
      <TabsWrapper>
        <LinksContainer>
          {tabs.map(({ key, label }) => (
            <LinkTab key={key} isActive={activeTab === key} onClick={() => setActiveTab(key)}>
              {label}
            </LinkTab>
          ))}
        </LinksContainer>
        {actions}
      </TabsWrapper>

      <SideWrapper isLoading={isLoading}>
        <ProfileImageWrapper>
          <ProfileImage
            onClick={handleProfileImageClick}
            $hasImage={!!image}
            $isVerified={isPhotoVerificationEnabled ? isVerified : undefined}>
            <ImageField
              key={currentImage || 'no-image'}
              value={currentImage}
              showUploadIcon={false}
              dropzoneRef={dropzoneRef}
              onChange={handleImageChange}>
              <NoGenderIcon />
            </ImageField>

            {isPhotoVerificationEnabled && image && (
              <Tooltip body={isVerified ? t('verifiedPhotoTooltip') : t('photoNotVerifiedTooltip')}>
                <ProfileImageHoverTarget />
              </Tooltip>
            )}
          </ProfileImage>

          {isPhotoVerificationEnabled && image && (
            <VerificationContainer>
              <Tooltip body={isVerified ? t('verifiedPhotoTooltip') : t('photoNotVerifiedTooltip')}>
                <VerificationBadge $isVerified={isVerified} onClick={handleProfileImageClick}>
                  {isVerified ? <VerifiedPhotoIcon /> : <UnverifiedPhotoIcon />}

                  <PhotoVerificationText $isVerified={isVerified}>
                    {isVerified ? t('verified') : t('unverified')}
                  </PhotoVerificationText>
                </VerificationBadge>
              </Tooltip>
            </VerificationContainer>
          )}
        </ProfileImageWrapper>
        <JustifyRow $isNew={mode === 'ADD'}>
          <Flex>
            <Title>{employeeDisplayName}</Title>
            {isPending && (
              <Tooltip body={t('pendingApproval')}>
                <CircleChip />
              </Tooltip>
            )}
          </Flex>
          <RatingRow>
            {rating > 0 && (
              <Tooltip body={t('basedOnEvents', { totalEvents: jobsRatingCount })} disabled={!jobsRatingCount}>
                <Flex>
                  {Array.from({ length: Math.round(rating) }).map((_, index) => (
                    <StarIconFilled key={index} />
                  ))}
                  {Array.from({ length: 5 - Math.round(rating) }).map((_, index) => (
                    <StarIcon key={index} />
                  ))}
                  <InfoText>{rating}</InfoText>
                </Flex>
              </Tooltip>
            )}
          </RatingRow>
        </JustifyRow>
        <Scrollable>
          {isPushNotification && (
            <Row>
              <ActivateIcon />
              <InfoText>{t('isPushNotification')}</InfoText>
            </Row>
          )}

          {lastActivity && (
            <Row>
              <DateIcon />
              <InfoText>
                {t('lastActivity')} <TimeAgoRenderer {...lastActivityProps} />
              </InfoText>
            </Row>
          )}

          {createdAt && (
            <Row>
              <CreatedIcon />
              <InfoText>
                {t('joined')} <TimeAgoRenderer {...createdAtProps} />
              </InfoText>
            </Row>
          )}

          {mode !== 'ADD' && <JobStatsRows employeeId={employeeId} firstName={firstName} />}

          {code && (
            <Row>
              <CodeIcon />
              <InfoText>
                {t('code')}: {code}
              </InfoText>
            </Row>
          )}

          <Row>{actions}</Row>
          <Links>
            {tabs.map(({ key, label }) => (
              <Link key={key} isActive={activeTab === key} onClick={() => setActiveTab(key)}>
                {label}
              </Link>
            ))}
          </Links>
        </Scrollable>
      </SideWrapper>
      {isImageModalOpen && (
        <ImageModal
          size="small"
          onClose={() => setIsImageModalOpen(false)}
          showCloseButton={false}
          showConfirmButton={false}
          usePortal>
          <ModalHeader>
            <ModalTitle>{employeeDisplayName}</ModalTitle>
            <CloseButton icon={CloseIcon} onClick={() => setIsImageModalOpen(false)} />
          </ModalHeader>
          <ModalContentArea>
            <img src={currentImage} alt="profile" />
          </ModalContentArea>
          {image && (
            <ModalFooter>
              {isPhotoVerificationEnabled && isVerified ? (
                <>
                  <PromptClick
                    title="unverifyPhotoTitle"
                    body={t('areYouSureUnverifyPhoto', { name: employeeDisplayName })}
                    confirmText={t('unverifyPhoto')}
                    onClick={handleTogglePhotoVerification}
                    isPermanentAction
                    enable>
                    <PhotoButton secondary disabled={isLoadingUpdateEmployee} type="button">
                      {t('unverifyPhoto')}
                    </PhotoButton>
                  </PromptClick>

                  <PhotoButton primary onClick={() => dropzoneRef.current?.open()} type="button">
                    {t('changePhoto')}
                  </PhotoButton>
                </>
              ) : (
                <>
                  <PhotoButton secondary onClick={() => dropzoneRef.current?.open()} type="button">
                    {t('changePhoto')}
                  </PhotoButton>

                  {isPhotoVerificationEnabled && (
                    <PromptClick
                      body={t('areYouSureVerifyPhoto')}
                      confirmText={t('verify')}
                      onClick={handleTogglePhotoVerification}
                      enable>
                      <PhotoButton primary disabled={isLoadingUpdateEmployee} type="button">
                        {t('verifyPhoto')}
                      </PhotoButton>
                    </PromptClick>
                  )}
                </>
              )}
            </ModalFooter>
          )}
        </ImageModal>
      )}
    </>
  );
};

export default Tabs;
